import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import { perfilPublicoPorToken, BASE } from "../services/api";

// PERFIL DO CANDIDATO POR LINK PERMANENTE (/candidato/:token).
// Existe porque o currículo VIAJA: a consultoria manda o PDF ao cliente dela, o recrutador encaminha internamente,
// e o arquivo é sempre um retrato do dia do envio. Este link leva ao perfil ATUAL e ao currículo original.
// Sem login de propósito — quem recebeu o PDF precisa conseguir abrir. Sem pretensão salarial, também de
// propósito: o valor que a pessoa pediu não é dado para circular junto com um documento encaminhado.
export default function CandidatoPublicoPage() {
  const { token } = useParams();
  const [p, setP] = useState(undefined); // undefined = carregando | null = não encontrado
  useEffect(() => {
    let vivo = true;
    perfilPublicoPorToken(token).then((d) => vivo && setP(d)).catch(() => vivo && setP(null));
    return () => { vivo = false; };
  }, [token]);

  if (p === undefined) return <><Header /><main className="max-w-3xl mx-auto px-4 py-10"><p className="text-gray-500">Carregando…</p></main></>;

  if (p === null) return (
    <>
      <Header />
      <main className="max-w-xl mx-auto px-4 py-10">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm">
          <div className="text-4xl">🔎</div>
          <h1 className="mt-2 text-xl font-bold text-gray-900">Perfil não encontrado</h1>
          <p className="mt-1 text-sm text-gray-600">Este link não corresponde a nenhum perfil. Ele pode ter sido digitado errado.</p>
          <Link to="/vagas" className="inline-block mt-4 text-sm text-primary font-semibold hover:underline">Ver vagas abertas →</Link>
        </div>
      </main>
    </>
  );

  const linha = [p.areaAtuacao, p.senioridade, p.cidade].filter(Boolean).join(" · ");
  return (
    <>
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-bold text-gray-900 break-words">{p.nome}</h1>
          {p.headline && <p className="text-gray-700 mt-0.5">{p.headline}</p>}
          {linha && <p className="text-sm text-gray-500 mt-1">{linha}</p>}
          {p.linkedinUrl && (
            <a href={p.linkedinUrl} target="_blank" rel="noopener noreferrer"
              className="inline-block mt-2 text-sm text-primary font-semibold hover:underline">LinkedIn →</a>
          )}

          {/* O ARQUIVO ORIGINAL. É o que se perdia quando a empresa recebia só o currículo padronizado:
              layout próprio, portfólio, o material que a pessoa preparou. */}
          {p.temCurriculo && (
            <a href={`${BASE}/perfil/publico/${token}/curriculo`} target="_blank" rel="noopener noreferrer"
              className="inline-block mt-4 rounded-xl bg-gray-900 hover:bg-black text-white text-sm font-semibold px-5 py-2.5">
              📄 Baixar currículo original
            </a>
          )}

          {p.resumo && (
            <section className="mt-6">
              <h2 className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Resumo profissional</h2>
              <p className="mt-1 text-gray-700 text-sm whitespace-pre-line">{p.resumo}</p>
            </section>
          )}

          {p.experiencias?.length > 0 && (
            <section className="mt-6">
              <h2 className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Experiência</h2>
              <div className="mt-2 space-y-3">
                {p.experiencias.map((e, i) => (
                  <div key={i} className="border-l-2 border-gray-200 pl-3">
                    <div className="font-semibold text-gray-900 text-sm">{e.cargo}{e.empresa ? ` · ${e.empresa}` : ""}</div>
                    {e.periodo && <div className="text-xs text-gray-500">{e.periodo}</div>}
                    {e.descricao && <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{e.descricao}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {p.skills?.length > 0 && (
            <section className="mt-6">
              <h2 className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Competências</h2>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {p.skills.map((s) => (
                  <span key={s} className="rounded-full bg-blue-50 text-blue-800 px-2.5 py-0.5 text-xs font-semibold">{s}</span>
                ))}
              </div>
            </section>
          )}

          {p.certificados?.length > 0 && (
            <section className="mt-6">
              <h2 className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Certificações</h2>
              <ul className="mt-1 text-sm text-gray-700 list-disc pl-5">
                {p.certificados.map((c, i) => <li key={i}>{c.nome || c}</li>)}
              </ul>
            </section>
          )}
        </div>

        {/* CONVITE AO RETORNO. Quem abre este link é quase sempre uma empresa avaliando a pessoa — inclusive
            empresas que nunca ouviram falar do eaiRH, porque o CV chega encaminhado. É a chance de fechar o
            ciclo que a plataforma existe para fechar: responder ao candidato, seja qual for a resposta. */}
        <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-5">
          <h2 className="font-bold text-blue-900">Vai dar um retorno a {p.nome?.split(" ")[0]}?</h2>
          <p className="text-sm text-blue-900/80 mt-1">
            No eaiRH a empresa responde ao candidato em qualquer etapa — inclusive para dizer que seguiu com outro perfil.
            É gratuito, leva menos de um minuto, e a pessoa é avisada na hora.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/entrar" className="rounded-xl bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-5 py-2.5">
              Responder pelo eaiRH
            </Link>
            <Link to="/vagas" className="rounded-xl border border-blue-300 text-blue-800 text-sm font-semibold px-5 py-2.5 hover:bg-blue-100">
              Publicar uma vaga (grátis)
            </Link>
          </div>
        </div>

        <p className="mt-3 text-[11px] text-gray-500 text-center">
          Perfil mantido por {p.nome?.split(" ")[0]} no <Link to="/" className="font-semibold hover:underline">eaiRH</Link> —
          reputação de empresas em processos seletivos. A pretensão salarial não é exibida nesta página.
        </p>
      </main>
    </>
  );
}
